import React, { useState, useCallback, useMemo, useRef, Ref, useEffect } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import styled from 'styled-components';
import { Privilege } from '../../api';

import { useSnackbar } from 'notistack';
import { CircularProgress, Tooltip } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { TSession } from './Sessions';
import { RouteComponentProps, withRouter } from 'react-router';
import AreYouSure from '../Util/AreYouSure';
import useHasPrivilege from '../Util/useHasPrivilege';
import LockIcon from '@material-ui/icons/Lock';
import sessionsService from '../../services/sessions';
import useSession from '../Util/useSession';
import { sessionLink } from './SessionDuplicateDialog';

interface Props extends RouteComponentProps {
  step?: string;
  sessionid: string;
  flow: string;
  canStartSession: boolean;
  steps: Array<TStep>;
  close: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    button: {
      marginRight: theme.spacing(1),
    },
    instructions: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    step: {
      borderRadius: '8px',
      padding: '4px',
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: "#ffd8a6",
      },
    },
    icon:{
      color: "#444",
      "&$completedIcon": {
        color: "#090",
      },
      "&$activeIcon": {
        color: "#ff9100",
      },
    },
    activeIcon: {},
    completedIcon: {}
  })
);

export type SessionStepProps = {
  sessionid: string;
  setNextEnabled: (enabled: boolean) => void;
  ref?: Ref<any>;
};

export interface SessionStaticProps {
  isComplete: (session: TSession) => boolean;
}
export type TStep = {
  step: string;
  title: string;
  component: React.RefForwardingComponent<any, SessionStepProps> & SessionStaticProps;
  filter?: (session: TSession) => boolean;
  requiredSteps?: string[];
};

function getStepContent(
  steps: Array<TStep>,
  step: number,
  setNextEnabled: (enabled: boolean) => void,
  sessionid: string,
  childRef: Ref<any>
) {
  if (step < steps.length) {
    const TagName = steps[step].component;
    return <TagName ref={childRef} sessionid={sessionid} setNextEnabled={setNextEnabled} />;
  }
  return null;
}

const SessionFlowBase = ({ sessionid, steps, step, close, canStartSession, history, flow }: Props) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = React.useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [selectedSessionVersion, setSelectedSessionVersion] = useState('orchestrator_csharp_v1');
  const [nextEnabled, setNextEnabled] = useState(false);
  const [isConfirmingSessionStart, setIsConfirmingSessionStart] = useState<boolean>(false);
  const [startSessionError, setStartSessionError] = useState<string | null>(null);
  const { session } = useSession(sessionid);
  const isAdmin = useHasPrivilege(Privilege.Admin);
  const duplicatedFromId = session?.duplicatedFromId ?? null;

  const childRef = useRef<{ onSave: () => Promise<boolean> }>();

  const theSteps = useMemo(() => {
    return (
      (session && steps.filter((step) => !step.filter || step.filter(session))) ||
      []
    );
  }, [steps, session]);

  useEffect(() => {
    for (let i = 0; i < theSteps.length; i++) {
      if (theSteps[i].step === step) {
        setActiveStep(i);
        break;
      }
    }
  }, [theSteps, step]);

  useEffect(() => {
    setStartSessionError(null);
  }, [activeStep]);

  const handleNext = useCallback(async () => {
    // If the child page has an onSave it must run successfully first!
    const onSave = childRef?.current?.onSave;
    let isOk = true;
    if (onSave) {
      isOk = await onSave();
    }
    if (isOk) {
      if (activeStep === theSteps.length - 1 && session?.id) {
        close();
      } else {
        history.push('/session/' + flow + '/' + sessionid + '/' + theSteps[activeStep + 1].step);
      }
    }
  }, [activeStep, sessionid, close, session, flow, history, theSteps]);

  const startSession = useCallback(
    async (includeBall: boolean = true, version: string = selectedSessionVersion) => {
      setIsConfirmingSessionStart(false);
      setStartSessionError(null);
      if (session?.id) {
        try {
          if (version.startsWith('csharp') || version.startsWith('orchestrator')) {

            const response = await sessionsService.startSessionRun({
              sessionId: sessionid,
              pipelineId: session.pipelineId,
              pipelineVersion: version
            });

            if (response?.data?.failures && response.data.failures.length > 0) {
              throw { response };
            }
          }
          else {
            // Implement other session versions as needed
          }

          enqueueSnackbar('Successfully started session', { variant: 'info', autoHideDuration: 3000 });
          setLoading(false);
          setStartSessionError(null);

          close();
        }
        catch (e: any)
        {
          let error = '';

          try
          {
            error = e?.response?.data?.failures?.map((x: { message: string }) => x.message.trim()).join(' | ');
          }
          catch 
          {
            // swallow
          }
          setNextEnabled(true);
          setLoading(false);
          setStartSessionError('Failed to start session' + (error ? `:\n${error}` : ''));
        }
      }
    },
    [
      session,
      sessionid,
      enqueueSnackbar,
      selectedSessionVersion,
      close,
    ]
  );

  const validateStartSession = useCallback((version: string) => {
    setSelectedSessionVersion(version);
    const anyTaskClaimed: boolean = session?.labelTasks?.find((t) => {
      return !!t.owner;
    }) !== undefined;
    if(anyTaskClaimed) {
      enqueueSnackbar('Cannot restart a session when a label task is claimed.', { variant: 'error', autoHideDuration: 3000 });
      return;
    }
    
    if (
      (session?.labelTasks && 
      session.labelTasks.length > 0)      
    ) {
      setIsConfirmingSessionStart(true);
    } else{
      startSession(true, version);
    }
  }, [session, enqueueSnackbar, startSession]);

  const handleBack = useCallback(() => {
    if (activeStep > 0) history.push('/session/' + flow + '/' + sessionid + '/' + theSteps[activeStep - 1].step);
    // setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }, [activeStep, sessionid, flow, history, theSteps]);

  const lastStep = activeStep === theSteps.length - 1;

  return (
    <Container className="sciui-card sciui-m-l sciui-p-l sciui-bg-p-5">
      <AreYouSure
        open={isConfirmingSessionStart}
        title="Re-run session resets label tasks"
        text="Re-running this session will delete all existing label tasks. Are you sure you want to delete them and create new ones?"
        onYes={() => {
              startSession();
        }}
        onNo={() => {
          setIsConfirmingSessionStart(false);
        }}
      />
      {duplicatedFromId && (
        <SessionHeader>
          <HeaderInfo>
            <span>
              Duplicate of <Link to={sessionLink(duplicatedFromId)}>#{duplicatedFromId}</Link>
            </span>
          </HeaderInfo>
        </SessionHeader>
      )}
      <StepperContainer>
        <Stepper activeStep={activeStep}>
          {theSteps.map((step) => {
            
            let isDisabled: boolean = false;
            let tooltipText: string = "Required steps to unlock: ";
            // Disable if we have a requiredStep that is not completed yet.
            if(step.requiredSteps !== undefined) {
              step.requiredSteps.forEach((reqStep: string) => {
                const foundStep: TStep | undefined = theSteps.find((step: TStep) => step.step === reqStep);
                if(foundStep) {
                  const stepComplete: boolean = session ? foundStep.component?.isComplete(session) : false 
                  if(!stepComplete && !isDisabled) {
                    isDisabled = true;
                    tooltipText += foundStep.title+" ";
                  }
                }
              });
            }

            const stepProps: { completed?: boolean } = { 
              completed: session && !isDisabled ? step.component?.isComplete(session) : false 
            };
            const labelProps: { optional?: React.ReactNode } = {};
            const url: string = step.step;

            return (
              <Step
                onClick={()=>!isDisabled && history.push('/session/'+flow+'/'+sessionid+'/'+url+'/')} 
                key={step.title} 
                {...stepProps}
                className={classes.step}
              >
                <StepLabel 
                  disabled={isDisabled}
                  onClick={()=>!isDisabled && history.push('/session/'+flow+'/'+sessionid+'/'+url+'/')} 
                  {...labelProps}
                  StepIconProps={{ classes:{ root: classes.icon, active: classes.activeIcon, completed: classes.completedIcon } }}
                >
                  { isDisabled ?
                      <Tooltip title={tooltipText} arrow>
                        <span>
                          {step.title}
                          <LockIcon fontSize="small" />
                        </span>
                      </Tooltip>
                    : step.title
                  }
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </StepperContainer>
      <ContentContainer>
        {getStepContent(theSteps, activeStep, setNextEnabled, session ? String(session.id) : '', childRef)}
      </ContentContainer>
      {startSessionError && lastStep && <InlineError role="alert">{startSessionError}</InlineError>}
      <ButtonContainer>
        <Button disabled={activeStep === 0} onClick={handleBack} className={classes.button}>
          Back
        </Button>
        <RightButtons>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            className={classes.button}
            disabled={!nextEnabled}>
            {loading ? <CircularProgress size={24} /> : lastStep ? 'Save' : 'Next'}
          </Button>

          {canStartSession && lastStep && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  validateStartSession('orchestrator_csharp_v1');
                }}
                className={classes.button}
                disabled={!nextEnabled}>
                {loading ? <CircularProgress size={24} /> : 'Start session'}
              </Button>
              {isAdmin && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    validateStartSession('orchestrator_v1');
                  }}
                  className={classes.button}
                  disabled={!nextEnabled}>
                  {loading ? <CircularProgress size={24} /> : 'Start session (py)'}
                </Button>
              )}
            </>
          )}
        </RightButtons>
      </ButtonContainer>
    </Container>
  );
};

const Container = styled.div`
  min-height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
`;

const SessionHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  min-height: 32px;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const StepperContainer = styled.div`
  flex-grow: 0;
`;

const ContentContainer = styled.div`
  flex-grow: 1;
  display: table;
`;

const RightButtons = styled.div`
  display: flex;
  flex-direction: row;
`;

const ButtonContainer = styled.div`
  flex-grow: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const InlineError = styled.div`
  color: #c62828;
  margin: 16px 0 8px;
`;

export default withRouter(SessionFlowBase);
